import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Fade,
  Typography,
  Paper,
} from "@mui/material";
import { useState } from "react";
import TextFormatIcon from "@mui/icons-material/TextFormat";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LooksOneIcon from "@mui/icons-material/LooksOne";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { enqueueSnackbar } from "notistack";
import axiosClient from "../../../../../../lib/axios";
import { useMutation, useQueryClient } from "react-query";

interface ModalAtributoUIProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  categoriaId: number;
}

interface AtributoCategoria {
  categoria_id: number;
  nome: string;
  obrigatorio: boolean;
  compoe_nome: boolean;
  tipo: string;
}

export default function ModalAtributoUI({ open, onClose, onSuccess, categoriaId }: ModalAtributoUIProps) {
  const [step, setStep] = useState(1);
  const [tipo, setTipo] = useState<string>('');
  const [nome, setNome] = useState('');
  const [obrigatorio, setObrigatorio] = useState(true);
  const [compoeNome, setCompoeNome] = useState(true);

  const queryClient = useQueryClient();
  const { mutate: createAttribute, isLoading: isCreatingAttribute } = useMutation({
    mutationKey: ['cadastrar-atributo', categoriaId],
    mutationFn: async (payload: AtributoCategoria) => {
      const response = await axiosClient.post('/estoque/categoria/cadastrar-atributo', payload);
      return response.data;
    },
    onSuccess: () => {
      enqueueSnackbar('Atributo cadastrado com sucesso!', { variant: 'success' });
      queryClient.invalidateQueries('categorias-mae');
      onClose();
      onSuccess();
    },
    onError: (error) => {
      console.error('Erro ao cadastrar atributo:', error);
      enqueueSnackbar('Erro ao cadastrar atributo', { variant: 'error' });
    },
    onSettled: () => {
      setStep(1);
      setTipo('');
      setNome('');
      setObrigatorio(true);
      setCompoeNome(true);
    },
  });

  const handleSubmit = () => {
    createAttribute({
      categoria_id: categoriaId,
      nome: nome,
      tipo: tipo,
      obrigatorio: obrigatorio,
      compoe_nome: compoeNome
    });
  };

  const handleTipoSelecionado = (tipoSelecionado: string) => {
    setTipo(tipoSelecionado);
    setStep(2);
  };

  const FieldCard = ({
    icon,
    label,
    onClick,
  }: {
    icon: any;
    label: string;
    onClick: () => void;
  }) => (
    <Paper
      onClick={onClick}
      elevation={0}
      sx={{
        cursor: "pointer",
        width: 110,
        height: 90,
        borderRadius: 2,
        bgcolor: "white",
        color: "#004D40",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        border: "1px solid #004D40",
        transition: "0.2s",
        "&:hover": {
          bgcolor: "#f5f5f5",
        },
      }}
    >
      {icon}
      <Typography fontSize={13} mt={1}>
        {label}
      </Typography>
    </Paper>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {/* ---------------------- TÍTULO ---------------------- */}
      <DialogTitle>
        {step === 1 && "Novo atributo"}
        {step === 2 && `Novo campo ${tipo}`}
        {step === 3 && "Novo campo tipo texto"}
      </DialogTitle>

      <DialogContent>
        {/* ---------------------- PASSO 1 ---------------------- */}
        <Fade in={step === 1}>
          <Box display={step === 1 ? "block" : "none"} mt={1}>
            <Box display="flex" gap={2} justifyContent="center" mt={2}>
              <FieldCard
                icon={<TextFormatIcon sx={{ fontSize: 32 }} />}
                label="Texto"
                onClick={() => handleTipoSelecionado('texto')}
              />
              <FieldCard
                icon={<CalendarTodayIcon sx={{ fontSize: 32 }} />}
                label="Data"
                onClick={() => handleTipoSelecionado('data')}
              />
              <FieldCard
                icon={<LooksOneIcon sx={{ fontSize: 32 }} />}
                label="Numérico"
                onClick={() => handleTipoSelecionado('numerico')}
              />
              <FieldCard
                icon={<AttachMoneyIcon sx={{ fontSize: 32 }} />}
                label="Moeda"
                onClick={() => handleTipoSelecionado('moeda')}
              />
            </Box>
          </Box>
        </Fade>

        {/* ---------------------- PASSO 2 ---------------------- */}
        <Fade in={step === 2}>
          <Box display={step === 2 ? "block" : "none"} mt={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={obrigatorio}
                  onChange={(e) => setObrigatorio(e.target.checked)}
                />
              }
              label="Campo obrigatório"
            />
            <br />
            <FormControlLabel
              control={
                <Switch
                  checked={compoeNome}
                  onChange={(e) => setCompoeNome(e.target.checked)}
                />
              }
              label="Compõe o nome"
            />

            <TextField
              fullWidth
              placeholder="Insira o nome do campo"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              sx={{ mt: 3 }}
            />
          </Box>
        </Fade>

      </DialogContent>

      {/* ---------------------- AÇÕES ---------------------- */}
      <DialogActions>
        <Button
          onClick={() => {
            if (step === 1) onClose();
            else setStep(step - 1);
          }}
        >
          {step === 1 ? "Cancelar" : "Voltar"}
        </Button>


        {step === 2 && (
          <Button variant="contained" onClick={handleSubmit}>
            Finalizar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export interface Atributo {
  categoria_id: number;
  nome: string;
  obrigatorio: boolean;
  compoe_nome: boolean;
  tipo: string;
}
